// lina-cart-bridge v2 — puts items lina stages in chat into the visitor's
// REAL site cart, live, without leaving the page.
//
// Channels:
//  1. Poll lina-cc /chat/cart-pushes (4s, only while the chat widget is open),
//     identified by the widget session id inside the cw_conversation JWT
//     cookie. The worker's consume is atomic, so with several tabs open
//     exactly ONE tab applies each push; it then broadcasts so the rest
//     refresh their drawers.
//  2. BroadcastChannel "lina-cart": refresh signal between tabs; also used by
//     the /cart/add fallback link, whose tab lands on ?lina_added=1,
//     broadcasts, and closes itself.
//
// All rendering funnels through ONE mutex-guarded routine that re-fetches the
// drawer + cart icon sections in root context and swaps the whole
// <cart-drawer> element — no incremental renderContents calls to race.
(() => {
  const WORKER = "https://lina-cc.dthake.workers.dev";
  const bc = "BroadcastChannel" in window ? new BroadcastChannel("lina-cart") : null;

  const sessionId = () => {
    try {
      const raw = (document.cookie.match(/cw_conversation=([^;]+)/) || [])[1];
      if (!raw) return null;
      const payload = JSON.parse(atob(decodeURIComponent(raw).split(".")[1]));
      return /^[a-f0-9-]{36}$/.test(payload.source_id || "") ? payload.source_id : null;
    } catch (e) {
      return null;
    }
  };

  const widgetOpen = () => {
    const holder = document.querySelector(".woot-widget-holder");
    return !!holder && !holder.classList.contains("woot--hide");
  };

  let refreshing = false;
  let refreshQueued = false;
  async function refreshCartUI() {
    if (refreshing) {
      refreshQueued = true; // collapse bursts into one trailing refresh
      return;
    }
    refreshing = true;
    try {
      // Root context on purpose: identical drawer markup on every page.
      const res = await fetch("/?sections=cart-drawer,cart-icon-bubble");
      if (!res.ok) return;
      const sections = await res.json();
      const parse = (html) => new DOMParser().parseFromString(html || "", "text/html");

      const freshDrawer = parse(sections["cart-drawer"]).querySelector("cart-drawer");
      const currentDrawer = document.querySelector("cart-drawer");
      if (freshDrawer && currentDrawer) {
        currentDrawer.replaceWith(freshDrawer);
        const d = document.querySelector("cart-drawer");
        if (d && typeof d.open === "function") d.open();
        else if (d) d.classList.add("animate", "active");
      } else if (location.pathname === "/cart") {
        location.reload();
        return;
      }

      const freshBubble = parse(sections["cart-icon-bubble"]).getElementById("cart-icon-bubble");
      const currentBubble = document.getElementById("cart-icon-bubble");
      if (freshBubble && currentBubble) currentBubble.replaceWith(freshBubble);
    } catch (e) {
      /* cosmetic only */
    } finally {
      refreshing = false;
      if (refreshQueued) {
        refreshQueued = false;
        setTimeout(refreshCartUI, 300);
      }
    }
  }

  async function applyPush(p) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: p.variant_id, quantity: p.quantity }] }),
    });
    if (!res.ok) return;
    await refreshCartUI();
    if (bc) bc.postMessage("refresh");
  }

  // Report page + REAL cart state (only when changed) so lina can resolve
  // "add THIS to my cart" and never mis-state the cart's contents or total.
  let lastStateSig = "";
  let lastReportAt = 0;
  async function reportState(sid) {
    try {
      const c = await fetch("/cart.js").then((r) => r.json());
      const items = c.items.slice(0, 30).map((i) => ({
        id: i.variant_id,
        quantity: i.quantity,
        title: (i.product_title || "").slice(0, 48),
        price: i.price,
      }));
      const state = {
        sid,
        url: location.href.split("#")[0].slice(0, 500),
        cart: { items, total_cents: c.total_price },
      };
      const sig = JSON.stringify([state.url, items.map((i) => [i.id, i.quantity]), c.total_price]);
      // Heartbeat: even an unchanged state re-reports every 60s, otherwise
      // the server-side freshness window expires while the visitor is still
      // sitting on the same page mid-conversation.
      if (sig === lastStateSig && Date.now() - lastReportAt < 60000) return;
      lastStateSig = sig;
      lastReportAt = Date.now();
      // text/plain keeps this a "simple request" — no CORS preflight.
      await fetch(`${WORKER}/chat/page-state`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(state),
      });
    } catch (e) {
      /* cosmetic only */
    }
  }

  async function poll() {
    if (!widgetOpen()) return;
    const sid = sessionId();
    if (!sid) return;
    await reportState(sid);
    try {
      const res = await fetch(`${WORKER}/chat/cart-pushes?sid=${sid}`);
      if (!res.ok) return;
      const data = await res.json();
      for (const p of data.pushes || []) await applyPush(p);
      if ((data.pushes || []).length) await reportState(sid); // cart just changed
    } catch (e) {
      /* worker unreachable — the tap-link fallback still works */
    }
  }

  if (bc) bc.onmessage = () => refreshCartUI();

  // Fallback tab: /cart/add?...&return_to=/cart?lina_added=1 lands here.
  if (new URLSearchParams(location.search).has("lina_added")) {
    if (bc) bc.postMessage("refresh");
    history.replaceState(null, "", location.pathname);
    setTimeout(() => window.close(), 400);
  }

  setInterval(poll, 4000);
})();
